import Events from '@/constants/events';
import { siteApi } from '@/services';
import {
  hasChanged,
  handleError,
  setItem,
  getItem,
  type CustomError,
} from '@/utilities';
import { consentState } from './consentState';
import { ComplianceRequest } from '@/types';

type ComplianceRequestsResponse = {
  items?: ComplianceRequest[];
  [key: string]: unknown;
};

type CachedComplianceData = {
  items: ComplianceRequest[];
  lastRefreshed: number;
};

class ComplianceRequests {
  complianceRequests: ComplianceRequest[];
  loading: boolean;
  lastRefreshed: number;
  REFRESH_THRESHOLD: number;

  constructor() {
    this.complianceRequests = [];
    this.loading = false;
    this.lastRefreshed = 0; // Timestamp of the last refresh, 0 means never
    this.REFRESH_THRESHOLD = 1000 * 60 * 60; // 1 hour
  }

  async refreshData({
    forceRefresh = false,
  }: { forceRefresh?: boolean } = {}): Promise<void> {
    try {
      // If already loading, don't start another refresh
      if (this.loading) {
        return;
      }

      // Use regionId from consentState which is already initialized
      // @ts-ignore Remove when consentState is properly typed
      const regionId = consentState?.regionId;
      if (!regionId) {
        return;
      }

      // Get cached data from localStorage if it exists
      const cachedData = getItem<CachedComplianceData>({
        key: `concord-compliance-requests-${regionId}`,
      });

      // If no cached data exists yet, initialize with empty array
      if (!cachedData) {
        setItem({
          key: `concord-compliance-requests-${regionId}`,
          value: { items: [], lastRefreshed: Date.now() },
        });

        // Set local state to empty array
        this.complianceRequests = [];

        // Dispatch loaded event for empty array
        window.dispatchEvent(new CustomEvent(Events.ComplianceRequestsLoaded));
      }

      // If we have cached lastRefreshed timestamp, use it
      if (cachedData?.lastRefreshed) {
        this.lastRefreshed = cachedData.lastRefreshed;
      }

      // If we have cached data
      if (
        cachedData &&
        Object.prototype.hasOwnProperty.call(cachedData, 'items')
      ) {
        // Check if we need to update our state
        if (hasChanged(cachedData.items || [], this.complianceRequests || [])) {
          this.complianceRequests = cachedData.items || [];
          window.dispatchEvent(
            new CustomEvent(Events.ComplianceRequestsChanged),
          );
        }

        // Dispatch loaded event for cached data
        window.dispatchEvent(new CustomEvent(Events.ComplianceRequestsLoaded));
      }

      // Check if we have any unresolved requests
      const hasUnresolvedRequests = this.complianceRequests?.some(
        (request) => request?.status !== 'resolved',
      );

      /* Auto-refresh condition: if we have data with unresolved requests 
      and it's been longer than the threshold since last API call */
      const shouldAutoRefresh =
        this.complianceRequests?.length > 0 &&
        hasUnresolvedRequests &&
        Date.now() - this.lastRefreshed > this.REFRESH_THRESHOLD;

      // Update forceRefresh flag if auto-refresh condition is met
      forceRefresh = forceRefresh || shouldAutoRefresh;

      // Only make API call if forced
      if (forceRefresh) {
        this.loading = true;
        window.dispatchEvent(new CustomEvent(Events.ComplianceRequestsLoading));

        const results =
          (await siteApi.getComplianceRequests()) as ComplianceRequestsResponse;

        // Update data timestamp after successful API call
        this.lastRefreshed = Date.now();

        // Only update our state if different from current state
        if (hasChanged(results?.items || [], this.complianceRequests || [])) {
          this.complianceRequests = results?.items || [];
          window.dispatchEvent(
            new CustomEvent(Events.ComplianceRequestsChanged),
          );
        }

        // Store in localStorage - even if items array is empty
        setItem({
          key: `concord-compliance-requests-${regionId}`,
          value: {
            items: results?.items || [],
            lastRefreshed: this.lastRefreshed,
          },
        });

        // Dispatch loaded event for API data
        window.dispatchEvent(new CustomEvent(Events.ComplianceRequestsLoaded));
      }
    } catch (err) {
      handleError(err as CustomError);
    } finally {
      this.loading = false;
    }
  }
}

export const complianceRequests = new ComplianceRequests();
